import type { PixelConfig } from '@/config';
import type { PixelEvent } from '@/events';
import type { AttributionIdentifiers } from '@/services/attribution/types';
import type { CustomerData } from '@/services/customer/types';
import { hasCustomerData } from '@/services/customer/types';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type {
  ApiBatchEventRequest,
  ApiEvent,
  ApiEventApp,
  ApiEventCategory,
  ApiEventIdentifiers,
} from './api-types';

function mapApp(config: PixelConfig): ApiEventApp {
  return {
    source: String(config.app.source ?? ''),
    target: String(config.app.target ?? ''),
    platform: String(config.app.platform ?? ''),
    environment: String(config.app.environment ?? ''),
  };
}

function generateEventId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function mapCategory(event: PixelEvent): ApiEventCategory {
  if ('category' in event && event.category) {
    if (event.category === 'ENGAGEMENT') return 'ENGAGEMENT';
    if (event.category === 'CONVERSION') return 'CONVERSION';
  }
  return 'OTHER';
}

function extractData(
  event: PixelEvent,
  customer?: CustomerData | null
): Record<string, unknown> | undefined {
  let data: Record<string, unknown> | undefined;

  if ('data' in event && event.data && typeof event.data === 'object') {
    data = { ...(event.data as Record<string, unknown>) };
  } else {
    const {
      type,
      timestamp,
      category,
      path,
      url,
      hostname,
      language,
      timezone,
      identifiers,
      queryParams,
      urlContext,
      customer: _customer,
      ...rest
    } = event as unknown as Record<string, unknown>;

    void type;
    void timestamp;
    void category;
    void path;
    void url;
    void hostname;
    void language;
    void timezone;
    void identifiers;
    void queryParams;
    void urlContext;
    void _customer;

    const keys = Object.keys(rest);
    data = keys.length > 0 ? rest : undefined;
  }

  if (hasCustomerData(customer)) {
    return { ...(data ?? {}), customer: { ...customer } };
  }

  return data;
}

function mapIdentifiers(
  identifiers: AttributionIdentifiers | undefined
): ApiEventIdentifiers {
  const base = {
    fbp: identifiers?.fbp ?? '',
    fbc: identifiers?.fbc ?? '',
    ga: identifiers?.ga ?? '',
    gcl_au: identifiers?.gclAu ?? '',
  };

  if (!identifiers) return base;

  return {
    ...base,
    fbclid: identifiers.fbclid ?? '',
    gclid: identifiers.gclid ?? '',
    gbraid: identifiers.gbraid ?? '',
    wbraid: identifiers.wbraid ?? '',
    gid: identifiers.gid ?? '',
    ttclid: identifiers.ttclid ?? '',
    ttp: identifiers.ttp ?? '',
    msclkid: identifiers.msclkid ?? '',
    li_fat_id: identifiers.liFatId ?? '',
    epik: identifiers.epik ?? '',
    customClickIds: identifiers.customClickIds ?? {},
    landingUrl: identifiers.landingUrl ?? '',
    referrer: identifiers.referrer ?? '',
    firstCollectedAt: identifiers.firstCollectedAt ?? 0,
    lastCollectedAt: identifiers.lastCollectedAt ?? 0,
  };
}

export class EventPayloadBuilder {
  private readonly context: PixelRuntimeContext;

  constructor(context: PixelRuntimeContext) {
    this.context = context;
  }

  buildApiEvent(event: PixelEvent, eventId?: string): ApiEvent {
    const config = this.context.config;
    const services = this.context.services;
    const metadata = services.browserMetadata.getMetadata();

    const visitorId =
      services.visitor.getVisitorId() ?? services.visitor.ensureVisitorId();

    const sessionId =
      services.session.getSessionId() ??
      `session_${config.identity.pixelId || 'anonymous'}_${Date.now()}`;

    const fingerprint =
      services.fingerprint.getFingerprint() ??
      services.fingerprint.ensureFingerprint();

    const identifiers =
      'identifiers' in event && event.identifiers
        ? event.identifiers
        : services.attribution?.getSnapshot();

    const customer =
      'customer' in event && event.customer
        ? event.customer
        : (services.customerIdentity?.getSnapshot() ?? null);

    return {
      id: eventId ?? generateEventId(),
      session_id: sessionId,
      visitor_id: visitorId,
      pixel_code: config.identity.pixelId,
      type: event.type as ApiEvent['type'],
      category: mapCategory(event),
      timestamp: String(event.timestamp ?? Date.now()),
      fingerprint,
      identifiers: mapIdentifiers(identifiers),
      page: {
        url: event.url ?? metadata.url ?? '',
        title: typeof document !== 'undefined' ? document.title : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        path: event.path ?? metadata.path ?? '',
        hostname: event.hostname ?? metadata.hostname ?? '',
      },
      device: {
        id: fingerprint,
        type: 'desktop',
        os: { name: 'Unknown', version: '' },
        browser: { name: 'Unknown', version: '' },
        engine: { name: 'Unknown', version: '' },
        userAgent: metadata.userAgent ?? '',
        language: event.language ?? metadata.language ?? '',
        screen: {
          width: metadata.screenWidth ?? 0,
          height: metadata.screenHeight ?? 0,
          pixel_ratio: 1,
        },
      },
      ...(hasCustomerData(customer) ? { customer: { ...customer } } : {}),
      data: extractData(event, customer),
      ...('queryParams' in event &&
      event.queryParams &&
      Object.keys(event.queryParams).length > 0
        ? { query_params: { ...event.queryParams } }
        : {}),
      ...('urlContext' in event && event.urlContext
        ? {
            url_context: {
              ...(event.urlContext.managed
                ? { managed: { ...event.urlContext.managed } }
                : {}),
              ...(event.urlContext.forwarded
                ? { forwarded: { ...event.urlContext.forwarded } }
                : {}),
              ...(event.urlContext.source
                ? { source: { ...event.urlContext.source } }
                : {}),
            },
          }
        : {}),
      app: mapApp(config),
      meta: {
        version: '2.0.0',
        pixel_source: 'pixel-v2',
        source: String(config.app.source ?? ''),
        target: String(config.app.target ?? ''),
        platform: String(config.app.platform ?? ''),
        environment: config.app.environment,
        script_url:
          typeof document !== 'undefined' && document.currentScript
            ? String((document.currentScript as HTMLScriptElement).src ?? '')
            : '',
        projectId: config.identity.projectId ?? '',
        pageId: config.identity.pageId ?? '',
      },
    };
  }

  buildBatchPayload(events: PixelEvent[]): ApiBatchEventRequest {
    const first = this.buildApiEvent(events[0] ?? ({} as PixelEvent));
    const apiEvents = events.map(event => this.buildApiEvent(event));

    return {
      pixel_code: this.context.config.identity.pixelId,
      visitor_id: first.visitor_id,
      session_id: first.session_id,
      batch_id: generateEventId(),
      sent_at: Date.now(),
      app: first.app,
      meta: first.meta,
      events: apiEvents,
    };
  }
}

export type { PixelConfig };
